import { useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { CheckCircleOutlined, ReloadOutlined, ShareAltOutlined } from '@ant-design/icons';

import { PageHeader } from '../../../components/shared';
import { Badge, Button } from '../../../components/ui';
import { useDuongDan } from '../../../hooks/useDuongDan';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { LuoiNguon } from '../components/chung/LuoiNguon';
import { KHOA_HIEN } from '../constants';
import { useDanhSachNguon } from '../hooks/useChung';
import { useDemKhuonTheoNguon } from '../hooks/useKhuon';
import type { GraphqlSource } from '../types';

/** Đường GỐC của màn khuôn một nguồn. */
export const khuonHref = (id: string) => `/tro-ly-ai/khuon/${encodeURIComponent(id)}`;

/** Nguồn trả lời theo khuôn: nguồn API luôn vậy, nguồn khác khi đã gạt công tắc sang khuôn. */
export const theoKhuon = (s: GraphqlSource) => s.kind === 'api' || s.cheDo === 'khuon';

/**
 * Truy vấn mẫu (`/tro-ly-ai/khuon`) — lưới nguồn; bấm một thẻ để sang màn khuôn của nguồn ấy.
 *
 * `?nguon=<mã>` là đường cũ từ thẻ nguồn và Kho dữ liệu — chuyển thẳng sang màn chi tiết, để link đã gửi đi không thành
 * một trang lưới phải tìm lại.
 */
export function KhuonView() {
  const { t, locale } = useT();
  const duongDan = useDuongDan();
  const nguonTs = useSearchParams()[0].get('nguon');
  const nguon = useDanhSachNguon(!nguonTs);
  const ds = useMemo(() => nguon.data ?? [], [nguon.data]);
  // Cùng khoá với `KhuonTab`, nên bấm vào thẻ là màn chi tiết có sẵn dữ liệu.
  const demQ = useDemKhuonTheoNguon(ds);
  const demTheoId = new Map(ds.map((s, i) => [s.id, demQ[i]]));

  if (nguonTs) return <Navigate to={duongDan(khuonHref(nguonTs))} replace />;

  const chan = (s: GraphqlSource) => {
    const q = demTheoId.get(s.id);
    if (q?.isError) return <span>{t('troLyAiKhuonView.khongDemDuoc')}</span>;
    if (!q?.data) return undefined;
    const tong = q.data.khuon.length;
    const sanSang = q.data.khuon.filter((k) => k.vaoBoChon).length;
    if (tong === 0) return <span>{t('troLyAiKhuonView.chuaCoKhuon')}</span>;
    return (
      <>
        <CheckCircleOutlined className={sanSang === tong ? 'text-success-600' : 'text-warning-600'} />
        {t('troLyAiKhuonView.demKhuon', { tong: soVN(tong, locale), sanSang: soVN(sanSang, locale) })}
      </>
    );
  };

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.troLiAi') }, { label: t('nav.muc.truyVanMau') }]}
          titleIcon={<ShareAltOutlined className="text-primary-600" />}
          title={t('troLyAiKhuonView.tieuDe')}
          subtitle={t('troLyAiKhuonView.phuDe')}
          actions={
            <Button
              variant="ghost"
              size="md"
              title={t('chung.lamMoi')}
              aria-label={t('chung.lamMoi')}
              onClick={() => void nguon.refetch()}
            >
              <ReloadOutlined className={nguon.isFetching ? 'animate-spin' : ''} />
            </Button>
          }
        />
        <LuoiNguon
          ds={ds}
          dangTai={nguon.isPending}
          loi={nguon.error}
          onThuLai={() => void nguon.refetch()}
          khoaHien={KHOA_HIEN.khuon}
          hrefCua={(s) => khuonHref(s.id)}
          boLoc={[
            {
              placeholder: t('troLyAi.luoiNguon.moiLoai'),
              options: [
                { value: 'csdl', label: t('troLyAi.luoiNguon.loaiCsdlVaGraphql'), khop: (s) => s.kind !== 'api' },
                { value: 'api', label: t('troLyAi.luoiNguon.loaiApi'), khop: (s) => s.kind === 'api' },
              ],
            },
            {
              placeholder: t('troLyAiKhuonView.moiCheDo'),
              options: [
                { value: 'khuon', label: t('troLyAiKhuonView.theoKhuon'), khop: theoKhuon },
                { value: 'cau_hoi', label: t('troLyAiKhuonView.theoBanKhai'), khop: (s) => !theoKhuon(s) },
              ],
            },
          ]}
          nhan={(s) => (
            <Badge variant={theoKhuon(s) ? 'primary' : 'neutral'}>
              {theoKhuon(s) ? t('troLyAiKhuonView.theoKhuon') : t('troLyAiKhuonView.theoBanKhai')}
            </Badge>
          )}
          chan={chan}
        />
      </div>
    </div>
  );
}
